import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { generateAIResponse } from "@/utils/api/ai-generation";

interface FormData {
  merchant: string;
  issue: string;
  amount: string;
  currency: string;
  messageFormat: "email" | "chat";
}

export const useSimplifiedRefundForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasUsedDemo, setHasUsedDemo] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    merchant: "",
    issue: "",
    amount: "",
    currency: "USD",
    messageFormat: "email"
  });

  // Check demo usage count
  useEffect(() => {
    const demoCount = parseInt(localStorage.getItem('demoRefundCount') || '0');
    setHasUsedDemo(demoCount >= 3);
  }, []);

  // Smart pre-fill from URL parameters and localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('refund-form-draft');
    const urlMerchant = searchParams.get('merchant');
    const urlIssue = searchParams.get('issue');
    const urlAmount = searchParams.get('amount');
    
    let initialData = { ...formData };
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        initialData = { ...initialData, ...parsed };
      } catch (e) {
        console.warn('Failed to parse saved form data');
      }
    }
    
    if (urlMerchant) initialData.merchant = decodeURIComponent(urlMerchant);
    if (urlIssue) initialData.issue = decodeURIComponent(urlIssue);
    if (urlAmount) initialData.amount = urlAmount;
    
    setFormData(initialData);
  }, [searchParams]);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.merchant || formData.issue || formData.amount) {
        localStorage.setItem('refund-form-draft', JSON.stringify(formData));
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [formData]);

  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFormatChange = (format: "email" | "chat") => {
    setFormData(prev => ({ ...prev, messageFormat: format }));
  };

  const handleQuickFill = (template: { merchant: string; issue: string; amount: string }) => {
    setFormData(prev => ({ 
      ...prev, 
      merchant: template.merchant,
      issue: template.issue,
      amount: template.amount
    }));
  };

  const createDemoRequest = async () => {
    try {
      const demoConversation = {
        id: `demo-${Date.now()}`,
        merchant: formData.merchant,
        product: formData.merchant,
        amount: parseFloat(formData.amount),
        issue: formData.issue,
        currency: formData.currency,
        status: "demo",
        message_format: formData.messageFormat,
        created_at: new Date().toISOString(),
      };

      const aiResponse = await generateAIResponse(
        "initial",
        demoConversation,
        [],
        undefined,
        "polite",
        true
      );

      const demoRequest = {
        ...demoConversation,
        messageFormat: formData.messageFormat,
        aiResponse: aiResponse?.response || "Your refund request has been generated successfully.",
        tone: aiResponse?.tone || "professional",
        legalContext: aiResponse?.legalContext,
      };

      sessionStorage.setItem('demoRefundRequest', JSON.stringify(demoRequest));
      
      // Increment demo usage count
      const currentCount = parseInt(localStorage.getItem('demoRefundCount') || '0');
      localStorage.setItem('demoRefundCount', (currentCount + 1).toString());
      
      return demoRequest;
    } catch (error) {
      console.error("Error creating demo request:", error);
      return {
        id: `demo-${Date.now()}`,
        merchant: formData.merchant,
        issue: formData.issue,
        amount: formData.amount,
        currency: formData.currency,
        messageFormat: formData.messageFormat,
        message_format: formData.messageFormat,
        product: formData.merchant,
        aiResponse: `Dear ${formData.merchant},\n\nI am writing to request a refund for my recent purchase. ${formData.issue}\n\nI would appreciate your prompt attention to this matter.\n\nThank you for your time and consideration.\n\nBest regards`,
        tone: "professional",
        status: "demo",
        created_at: new Date().toISOString(),
      };
    }
  };

  const handleSubmit = async () => {
    if (!formData.merchant || !formData.issue || !formData.amount) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    // Check demo usage count (allow up to 3 uses)
    const demoCount = parseInt(localStorage.getItem('demoRefundCount') || '0');
    if (!user && demoCount >= 3) {
      toast({
        title: "Demo limit reached",
        description: "Sign up to create unlimited refund requests",
        variant: "default"
      });
      navigate('/auth', { 
        state: { 
          showSignUp: true,
          from: { pathname: '/' }
        } 
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (user) {
        const { data, error } = await supabase
          .from("refund_requests")
          .insert({
            user_id: user.id,
            merchant: formData.merchant,
            product: formData.merchant,
            amount: parseFloat(formData.amount),
            issue: formData.issue,
            status: "pending",
            message_format: formData.messageFormat,
          })
          .select()
          .single();

        if (error) throw error;

        await supabase.from("messages").insert({
          refund_request_id: data.id,
          sender_type: "user",
          content: formData.issue,
        });

        localStorage.removeItem('refund-form-draft');
        setIsSubmitted(true);
        
        setTimeout(() => {
          navigate(`/conversation/${data.id}`);
        }, 2000);
        
      } else {
        await createDemoRequest();
        localStorage.removeItem('refund-form-draft');
        setIsSubmitted(true);
        
        setTimeout(() => {
          navigate('/preview');
        }, 2000);
      }
      
    } catch (error) {
      console.error("Error creating refund request:", error);
      toast({
        title: "Error",
        description: "Failed to create refund request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    isSubmitting,
    isSubmitted,
    hasUsedDemo,
    handleFieldChange,
    handleFormatChange,
    handleQuickFill,
    handleSubmit
  };
};
